// Edit layer style > Rule > Geometry > Color Scheme > Gradient

import { Box, Button, IconButton, MenuItem, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { theme_orange, theme_textColorBlended, theme_textColorMain } from '../../../Theme';
import { ILayerStyleRule_Geometry, ILayerStyleType_Gradient, ILayerStyleType_GradientItem } from './LayerStyleInterfaces';
import { ColorGradientPresets, GRADIENT_PRESET_EMPTY_ID } from '../../../Aois/AoiGroupAndPortfolioMap/ColorSchemeEditor/ColorGradientPresets';
import { CustomTextField } from '../EditLayer';
import { CustomSelect, MuiColorInputStyled } from '../EditLayerStyle';
import { ChangeEvent, ReactNode, useState } from 'react';
import Debug from '../../../Debug';
import { GetLayerAttribMinMax  } from './EditLayerStyle_ColorScheme_Classified';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { ToastNotification } from '../../../ToastNotifications';
import BigNumber from "bignumber.js";
import { MuiColorInputColors } from 'mui-color-input';
import { ColorGradientBar } from '../../../Aois/AoiGroupAndPortfolioMap/ColorSchemeEditor/ColorGradientBar';


export const LAYER_STYLE_GRADIENT_DEFAULT_STOP_COUNT = 2;
export const LAYER_STYLE_GRADIENT_DEFAULT_PRESET_ID = 3; // Yellow-Orange-Red (Continuous)

const MIN_GRADIENT_STOPS = 2;
const MAX_GRADIENT_STOPS = 10;


//-------------------------------------------------------------------------------
// Component props
//-------------------------------------------------------------------------------
export interface EditLayerStyle_ColorScheme_Gradient_Props
{
  //layer?: ILayer;
  ruleGeom?: ILayerStyleRule_Geometry;   // input (if undefined, there is no geom section in the rule)
  OnRuleGradientUpdated: any;      // output
}

//-------------------------------------------------------------------------------
// Edit layer style > Rule > Geometry > Color Scheme > Gradient
//-------------------------------------------------------------------------------
export function EditLayerStyle_ColorScheme_Gradient(props: EditLayerStyle_ColorScheme_Gradient_Props) 
{


  // Keeps track of which value/color item is being edited.  For performance reasons, only one item 
  // can be edited at a time.
  const [itemBeingEdited, setItemBeingEdited] = useState<ILayerStyleType_GradientItem|undefined>(undefined);







  //-------------------------------------------------------------------------------
  // Enter EDIT mode for a specific class item.
  //-------------------------------------------------------------------------------
  function OnEnterEditMode(classifiedItem: ILayerStyleType_GradientItem)
  {
    setItemBeingEdited(classifiedItem);
  }

  //-------------------------------------------------------------------------------
  // Exit EDIT mode.
  //-------------------------------------------------------------------------------
  function OnExitEditMode()
  {
    setItemBeingEdited(undefined);
  }

  //-------------------------------------------------------------------------------
  // A new gradient preset was selected.
  //-------------------------------------------------------------------------------
  async function OnPresetChanged(gradient_preset: ILayerStyleType_Gradient | undefined)
  {
    if(!props.ruleGeom || !props.ruleGeom.gradient) return;

    setItemBeingEdited(undefined);

    let new_gradient: ILayerStyleType_Gradient | undefined = undefined;

    if(gradient_preset && gradient_preset.id !== GRADIENT_PRESET_EMPTY_ID)  // Colors based on gradient preset
      new_gradient = await LayerStyle_CreateGradientStops_GradientPreset(props.ruleGeom, props.ruleGeom.gradient.items.length, gradient_preset);
    else // No colors assigned
       new_gradient = await LayerStyle_CreateGradientStops_NoColors(props.ruleGeom, props.ruleGeom.gradient.items.length);

    if(!new_gradient)
      return;

    // Notify caller of the change
    props.OnRuleGradientUpdated(new_gradient);
  }

  //-------------------------------------------------------------------------------
  // The number of gradient stops has changed.
  //-------------------------------------------------------------------------------
  const OnGradientStopCountChanged = async (event: SelectChangeEvent<unknown>, child: ReactNode) => 
  {
    if(!props.ruleGeom || !props.ruleGeom.gradient) return;

    setItemBeingEdited(undefined);

    const newClassCount: number = Number.parseInt(event.target.value as string);
    if(isNaN(newClassCount) || newClassCount < MIN_GRADIENT_STOPS || newClassCount > MAX_GRADIENT_STOPS)
    {
      Debug.error('OnGradientStopCountChanged> Value is invalid or out of bounds');
      return;
    }

    // When the class count is manually changed, the gradient preset is ignored, and no colors are set.
    // (Gradient presets each have their own number of color stops built-in).

    let new_gradient: ILayerStyleType_Gradient | undefined = await LayerStyle_CreateGradientStops_NoColors(props.ruleGeom, newClassCount);
    if(!new_gradient)
      return;

    // Notify caller of the change
    props.OnRuleGradientUpdated(new_gradient);
  }

  //-------------------------------------------------------------------------------
  // Remove gradient stop item button was pushed.
  //-------------------------------------------------------------------------------
  function OnRemoveGradientStop(classifiedItem: ILayerStyleType_GradientItem)
  {
    if(!props.ruleGeom || !props.ruleGeom.gradient) return;

    // Create an updated item

    const updated_gradient: ILayerStyleType_Gradient = 
    {
      ...props.ruleGeom.gradient,
      items: props.ruleGeom.gradient.items.filter(oldItem => oldItem.id !== classifiedItem.id),
      preset_id: GRADIENT_PRESET_EMPTY_ID,
    }

    // Notify caller of the change
    props.OnRuleGradientUpdated(updated_gradient);
  }

  //-------------------------------------------------------------------------------
  // Returns TRUE if the specified item is either first or last.
  //-------------------------------------------------------------------------------
  function IsFirstOrLastItem(itemIndex: number): boolean
  {
    if (itemIndex===0) 
      return true;

    if(!props.ruleGeom || !props.ruleGeom.gradient) 
      return false;

    return itemIndex===props.ruleGeom.gradient.items.length-1;
  }

  //-------------------------------------------------------------------------------
  // A gradient color has changed.
  //-------------------------------------------------------------------------------
  function OnGradientColorChanged(value: string, colors: MuiColorInputColors, 
                                  gradientItem: ILayerStyleType_GradientItem)
  {
    if(!props.ruleGeom || !props.ruleGeom.gradient) return;

    // Create an updated gradient item
    const updated_gradient_item: ILayerStyleType_GradientItem =
    {
      ...gradientItem,
      color: value,
    }

    // Replace it in the array
    const updated_gradient_items: ILayerStyleType_GradientItem[] = props.ruleGeom.gradient.items.map(oldItem => oldItem.id === gradientItem.id ? updated_gradient_item : oldItem);

    // Update the 'items' array
    const updated_gradient: ILayerStyleType_Gradient = 
    {
      ...props.ruleGeom.gradient,
      preset_id: undefined,
      items: updated_gradient_items
    }

    // Notify caller of the change
    props.OnRuleGradientUpdated(updated_gradient);
  }

  //-------------------------------------------------------------------------------
  // An item's VALUE value has changed.
  //-------------------------------------------------------------------------------
  function OnGradientStopValueTextFieldChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, 
                                      gradientItem: ILayerStyleType_GradientItem,
                                      itemIndex: number): void
  {
    if(!props.ruleGeom || !props.ruleGeom.gradient) return;

    if(itemIndex === 0 || itemIndex === props.ruleGeom.gradient.items.length-1) return; // We don't allow editing the first or last values

    const newValueStr: string = event.target.value as string;
    const newValueNum: number = Number.parseFloat(newValueStr);
    
    // Create an updated item

    const updated_item: ILayerStyleType_GradientItem = 
    {
      ...gradientItem,
      value: newValueNum,
    }

    const updated_gradient: ILayerStyleType_Gradient = 
    {
      ...props.ruleGeom.gradient,
      items: props.ruleGeom.gradient.items.map(oldItem => oldItem.id === gradientItem.id ? updated_item : oldItem),
      preset_id: GRADIENT_PRESET_EMPTY_ID,
    }

    // Notify caller of the change
    props.OnRuleGradientUpdated(updated_gradient);
  }

  //-------------------------------------------------------------------------------
  // Reverses the current list of colors.
  // The values are unchanged.
  //-------------------------------------------------------------------------------
  function OnReverseColors()
  {
    if(!props.ruleGeom || !props.ruleGeom.gradient || !props.ruleGeom.gradient.items ||
       props.ruleGeom.gradient.items.length === 0) 
     return;

    setItemBeingEdited(undefined);

    // Create a new array of all the colors, but in reverse order

    const reversedColors: string[] = [];
    for(let i=props.ruleGeom.gradient.items.length-1; i >= 0; i--)
      reversedColors.push(props.ruleGeom.gradient.items[i].color);

    // Rebuild the color items, but using the new reversed colors

    const new_gradient_items: ILayerStyleType_GradientItem[] = [];
    for(let i=0; i < props.ruleGeom.gradient.items.length; i++)
      new_gradient_items.push(
        { 
          ...props.ruleGeom.gradient.items[i],
          color: reversedColors[i]
        });

    // Create an update 'gradient' item

    const updated_gradient: ILayerStyleType_Gradient = 
    {
      ...props.ruleGeom.gradient,
      items: new_gradient_items
    }

    // Notify caller of the change
    props.OnRuleGradientUpdated(updated_gradient);
  }

  //-------------------------------------------------------------------------------
  // Auto-refresh values only (the number of gradient stops and the colors are not changed).
  //-------------------------------------------------------------------------------
  async function OnRefreshValues()
  {
    if(!props.ruleGeom || !props.ruleGeom.gradient || !props.ruleGeom.gradient.items) return;

    setItemBeingEdited(undefined);

    // Get min and max values

    const minMaxValues: { min_value: number, max_value: number } | undefined = await GetLayerAttribMinMax(props.ruleGeom.attribute_id);
    if(minMaxValues === undefined)
    {
      ToastNotification('error', 'Unable to get min/max values for this attribute');
      return undefined;
    }

    // NOTE:  Doing the math using the JS primitive 'number' often leads to weird
    //        inaccuracy in the values produced.  So instead we use the BigNumber
    //        external library to produce more accurate numbers.
    const minValue_big = new BigNumber(minMaxValues.min_value);
    const maxValue_big = new BigNumber(minMaxValues.max_value);

    const numColorStops: number = props.ruleGeom.gradient.items.length;

    //const valueStep = (maxValue - minValue) / (props.colorScheme.gradient.items.length-1);
    const valueStep_big = maxValue_big.minus(minValue_big).dividedBy(numColorStops-1);

    // Rebuild the gradient stops, but using the new values

    const new_gradient_items: ILayerStyleType_GradientItem[] = [];
    for(let i=0; i < numColorStops; i++)
      new_gradient_items.push(
        { 
          ...props.ruleGeom.gradient.items[i],
          //value: minValue + i * valueStep,
          value: minValue_big.plus(valueStep_big.times(i)).toNumber(), 
        });

    const updated_gradient: ILayerStyleType_Gradient =
    {
      ...props.ruleGeom.gradient,
      items: new_gradient_items
    }

    // Notify caller of the change
    props.OnRuleGradientUpdated(updated_gradient);
  }  













  

  if(!props.ruleGeom || !props.ruleGeom.gradient || !props.ruleGeom.attribute_id) return null;


  // Main render

  return (

    <Stack sx={{ mx: 0, mt: 2 }}>

      <Typography sx={{ mt: 0, mb: 2, color: theme_textColorMain+'70', fontSize: '0.7rem' }}>
        There are two ways to set up the gradient color stops - automatically or manually.  Use the <i>Gradient Preset</i> drop-down to automatically generate all color stop values and colors, or use the <i>Gradient Stop Count</i> to set up the stop values but manually enter in all the colors.  You can also manually edit all the stop values except the first and last.
      </Typography>

      <Stack direction='row' sx={{ mb: 2, alignItems: 'center', justifyContent: 'left' }}>

        {/* Select number of gradient stops */}

        <Stack direction='column' sx={{ width: '150px' }}>

          <Typography sx={{ color: theme_textColorBlended, opacity: 0.8, fontSize: '0.7rem' }}>
            Gradient Stop Count
          </Typography>

          <CustomSelect variant='standard' size='small'
                        value={props.ruleGeom.gradient.items.length}
                        disabled={!props.ruleGeom.gradient || !props.ruleGeom.attribute_id}
                        onChange={OnGradientStopCountChanged}
                        sx={{ p: 0.5 }}>

            {Array.from({ length: MAX_GRADIENT_STOPS - MIN_GRADIENT_STOPS + 1 }, (_, i) => MIN_GRADIENT_STOPS + i).map(num =>
              <MenuItem key={num} value={num} sx={{ color: theme_textColorBlended, fontSize: '1.0rem' }}>{num}</MenuItem>
            )}

          </CustomSelect>
        </Stack>

        {/* Gradient presets */}

        <Stack sx={{ ml: 5, width: '100%' }}>
          <ColorGradientPresets allowEmptySelection={true}
                                selectedPresetID={props.ruleGeom.gradient.preset_id}
                                OnPresetChanged={OnPresetChanged} />
        </Stack>

      </Stack>

      {/* Color gradient bar (not shown if a preset is active) */}
      {props.ruleGeom.attribute_id && props.ruleGeom.gradient.items.length >= 2 && (!props.ruleGeom.gradient.preset_id || props.ruleGeom.gradient.preset_id===GRADIENT_PRESET_EMPTY_ID)
        ?
          <Stack sx={{ my: 1, height: '18px' }}>
            <ColorGradientBar gradient={props.ruleGeom.gradient} />
          </Stack>
        :null
      }

      {/* Utility buttons */}

      <Stack direction='row' sx={{ my: 1, alignItems: 'center'}}>

        <Button variant='outlined' sx={{ ml: 0, textTransform: 'none' }}
                onClick={(_)=>OnRefreshValues()}>
          <Stack direction='column'>
            <Typography sx={{ fontSize: '0.8rem', color: theme_textColorBlended }}>
              Refresh Values
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: theme_textColorMain, opacity: 0.5 }}>
              Linear, colors unchanged
            </Typography>
          </Stack>
        </Button>

        <Button variant='outlined' sx={{ ml: 2, textTransform: 'none' }}
                onClick={(_)=>OnReverseColors()}>
          <Stack direction='column'>
            <Typography sx={{ fontSize: '0.8rem', color: theme_textColorBlended }}>
              Reverse Colors
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: theme_textColorMain, opacity: 0.5 }}>
              Values unchanged
            </Typography>
          </Stack>
        </Button>

      </Stack>

      {/* If the value ranges were auto-generated (no manal editing), show the range interval (and units) */}

      {/* {props.ruleGeom.gradient.auto_generated_values_interval
        ?
          <Stack direction='row' sx={{ mt: 2, mb: 0.5, alignItems: 'center' }}>
            <Typography sx={{ fontSize: '1rem', color: theme_textColorBlended }}>
              Auto-Interval:
            </Typography>
            <Typography sx={{ ml: 0.8, fontSize: '1rem', color: theme_limeGreen, opacity: 0.7, fontWeight: 'bold' }}>
              {FriendlyNumber(props.ruleGeom.gradient.auto_generated_values_interval,3)}
            </Typography>
            <Typography sx={{ ml: 0.6, fontSize: '1rem', color: theme_textColorBlended }}>
              {GetVectorLayerAttributeByID(props.layer, props.ruleGeom.attribute_id)?.unit}
            </Typography>
          </Stack>
        :null
      } */}

      <Typography sx={{ mb: 1, fontSize: '0.7rem', color: theme_textColorMain+'70' }}>
        The values must be continuous, sorted in ascending order, and cover the entire range of the attribute's value.  The min and max values cannot be changed.  If you need to restrict any values please use the <i>Attribute Filter</i> feature above.
      </Typography>

      {/* All classes and their colors */}

      {props.ruleGeom.gradient.items.map(function(gradientItem,itemIndex)
      {
        if(itemBeingEdited?.id === gradientItem.id)
        return (

          // EDIT MODE

          <Stack key={gradientItem.id} direction='row' 
                 sx={{ my: 0.5, mx: '-10px', width: 'calc(100% + 10px)', alignItems: 'center', bgcolor: theme_textColorBlended+'30', 
                       borderRadius: 2, py: 1, pl: 1, justifyContent: 'space-between' }}>

            <Stack direction='row' sx={{ alignItems: 'center', width: '100%', justifyContent: 'start' }}>

              {/* Value edit box */}

              <Stack sx={{ width: '120px' }}>
                <Typography sx={{ color: theme_textColorBlended, fontSize: '0.8rem' }}>
                  Value
                </Typography>

                <CustomTextField variant='standard' size='small' autoComplete='off'
                                 disabled={IsFirstOrLastItem(itemIndex)}
                                 value={gradientItem.value}
                                 inputProps={{ type: 'number' }} 
                                 onChange={(e)=>OnGradientStopValueTextFieldChanged(e,gradientItem,itemIndex)}
                                 sx={{ p: 0 }}/>
              </Stack>

            </Stack>

            <Stack direction='row' sx={{ alignItems: 'center' }}>

              {/* Color editor */}

              <Stack sx={{ ml: 2 }}>
                <Typography sx={{ color: theme_textColorBlended, fontSize: '0.8rem' }}>
                  Color
                </Typography>
                <MuiColorInputStyled variant='standard' size='small' format='hex' isAlphaHidden={true}
                                     value={gradientItem.color}
                                     onChange={(v,c)=>OnGradientColorChanged(v,c,gradientItem)}
                                     sx={{ width: '120px' }}/>
              </Stack>

              {/* Delete button */}

              <IconButton sx={{p: 0.3, ml: 1, opacity: IsFirstOrLastItem(itemIndex) ? 0 : 1 }} 
                          onClick={(_)=>OnRemoveGradientStop(gradientItem)}
                          disabled={IsFirstOrLastItem(itemIndex)}>
                <DeleteIcon sx={{ color: theme_textColorBlended, opacity: 0.7, p: 0.4 }}/>
              </IconButton>

              {/* Edit button (in EDIT mode) */}

              <IconButton sx={{p: 0.3, ml: 1}} onClick={(_)=>OnExitEditMode()}>
                <EditIcon sx={{ color: theme_orange, opacity: 0.7, p: 0.4 }}/>
              </IconButton>

            </Stack>

          </Stack>                    
        )

        else // VIEW MODE
        return (
  
            <Stack key={gradientItem.id} direction='row' sx={{ my: 0, width: '100%', alignItems: 'center' }}>
              
              <Stack direction='row' sx={{ alignItems: 'center', width: '100%', justifyContent: 'start' }}>

                <Typography sx={{ width: '120px', fontSize: '0.9rem', color: theme_textColorMain+'A0' }}>
                  {parseFloat(gradientItem.value.toFixed(2))}
                </Typography>
                {/* <Typography sx={{ ml: 2, width: '120px', fontSize: '0.9rem', color: theme_textColorMain+'A0' }}>
                  {parseFloat(classifiedItem.value_max.toFixed(2))}
                </Typography> */}

              </Stack>

              <Stack direction='row' sx={{ mr: '50px', width: '150px', alignItems: 'center' }}>
                <Box sx={{ bgcolor: gradientItem.color, width: '20px', height: '20px', borderRadius: 1, border: 1, borderColor: 'black' }}/>
                {gradientItem.color && gradientItem.color !== '' 
                  ? 
                    <Typography sx={{ ml: 1, color: theme_textColorMain+'A0' }}>
                      {gradientItem.color}
                    </Typography>
                  :
                    <Typography sx={{ ml: 1, fontSize: '0.8rem', color: theme_textColorBlended+'90' }}>
                      No color set
                    </Typography>
                }
              </Stack>

              {/* Edit button (in VIEW mode) */}

              <IconButton sx={{p: 0.3, ml: 1 }} onClick={(_)=>OnEnterEditMode(gradientItem)}>
                <EditIcon sx={{ color: theme_textColorBlended, opacity: 0.7, p: 0.4 }}/>
              </IconButton>

            </Stack>
        )
      })}

      {/* Button to add a new gradient stop item (manually) */}

      {/* <Stack sx={{ mt: 1, alignItems: 'start', width: '100%' }}>
        <Button variant='outlined' onClick={()=>OnAddNewClass()}
                sx={{ textTransform: 'none', borderRadius: 2, px: 1, py: 0.2, fontSize: '0.7rem', opacity: 0.8 }}>
          Add a New Gradient Stop
        </Button>      
      </Stack> */}






    </Stack>
  )
}




//-------------------------------------------------------------------------------
// Get default color scheme: GRADIENT
//-------------------------------------------------------------------------------
export function GetDefaultLayerStyle_Gradient(): ILayerStyleType_Gradient
{
  const defaultItem: ILayerStyleType_Gradient =
  {
    id: 1,
    name: undefined,
    items: [],
    preset_id: GRADIENT_PRESET_EMPTY_ID,
  }

  return defaultItem;
}

//-------------------------------------------------------------------------------
// Creates gradient stops for the specified gradient color scheme.
// Colors are left blank/undefined.
//-------------------------------------------------------------------------------
export async function LayerStyle_CreateGradientStops_NoColors(
                                       styleGeom: ILayerStyleRule_Geometry, 
                                       numColorStops: number): Promise<ILayerStyleType_Gradient | undefined>
{
  // Get min and max values

  const minMaxValues: { min_value: number, max_value: number } | undefined = await GetLayerAttribMinMax(styleGeom.attribute_id);
  if(minMaxValues === undefined)
  {
    ToastNotification('error', 'Unable to get min/max values for this attribute');
    return undefined;
  }

  // NOTE:  Doing the math using the JS primitive 'number' often leads to weird
  //        inaccuracy in the values produced.  So instead we use the BigNumber
  //        external library to produce more accurate numbers.
  const minValue_big = new BigNumber(minMaxValues.min_value);
  const maxValue_big = new BigNumber(minMaxValues.max_value);

  const valueStep_big = maxValue_big.minus(minValue_big).dividedBy(numColorStops-1);

  const new_gradient_items: ILayerStyleType_GradientItem[] = [];
  let nextID = 1;

  for(let i=0; i < numColorStops; i++)
  {
    new_gradient_items.push(
      { 
        id: nextID++, 
        value: minValue_big.plus(valueStep_big.times(i)).toNumber(), 
        color: ''
      });
  }

  const new_gradient: ILayerStyleType_Gradient =
  {
    id: 1,
    name: undefined,
    preset_id: undefined,
    items: new_gradient_items
  }

  // Success
  return new_gradient;
}

//-------------------------------------------------------------------------------
// Creates ranges for the specified gradient color scheme.
// Assigns colors based on the specified gradient preset.
//-------------------------------------------------------------------------------
export async function LayerStyle_CreateGradientStops_GradientPreset(
                                       styleGeom: ILayerStyleRule_Geometry, 
                                       numColorStops: number,
                                       gradient_preset: ILayerStyleType_Gradient | undefined): Promise<ILayerStyleType_Gradient | undefined>
{
  if(!gradient_preset)
    return undefined;

  // Apply the new colors stops to the color scheme

  // The local gradient is based on a 0-100 value scale.  Before we apply the new
  // gradient to the color scheme, we must re-scale the values of each stop to the 
  // attribute's actual min/max values.


  // Get min and max values

  const minMaxValues: { min_value: number, max_value: number } | undefined = await GetLayerAttribMinMax(styleGeom.attribute_id);
  if(minMaxValues === undefined)
  {
    ToastNotification('error', 'Unable to get min/max values for this attribute');
    return undefined;
  }

  // NOTE:  Doing the math using the JS primitive 'number' often leads to weird
  //        inaccuracy in the values produced.  So instead we use the BigNumber
  //        external library to produce more accurate numbers.
  const minValue_big = new BigNumber(minMaxValues.min_value);
  const maxValue_big = new BigNumber(minMaxValues.max_value);


  const new_items: ILayerStyleType_GradientItem[] = [];

  for(let i=0; i < gradient_preset.items.length; i++)
  {
    // The preset gradients are based on a percent (0-100) scale.  Before we apply it, we must
    // re-scale the value based on our attribute's min/max.
    //
    // NOTE:  Doing the math using the JS primitive 'number' often leads to weird
    //        inaccuracy in the values produced.  So instead we use the BigNumber
    //        external library to produce more accurate numbers.

    //const scaledValue = ((maxValue - minValue) * gradient_preset.items[i].value / 100.0) + minValue;
    const scaledValue_big: BigNumber = maxValue_big.minus(minValue_big).times(gradient_preset.items[i].value).dividedBy(100).plus(minValue_big);

    new_items.push(
      {
        ...gradient_preset.items[i],
        value: scaledValue_big.toNumber(),
      })
  }

  const new_gradient: ILayerStyleType_Gradient =
  {
    id: 1,
    name: undefined,
    preset_id: gradient_preset.id,
    items: new_items
  }
    
  // Success
  return new_gradient;
}

//-------------------------------------------------------------------------------
// Validates the specified rule geometry GRADIENT color scheme.
//
// Works for both regular rules and the special global defaults rule.
// 
// Returns an error message if validation FAILS, or empty string if it succeeds.
//-------------------------------------------------------------------------------
export function ValidateLayerStyleRuleGeom_Gradient(gradient: ILayerStyleType_Gradient | undefined, 
                                                    errMsgPrefix: string): string
{
  if(gradient === undefined)
    return errMsgPrefix + 'Invalid color scheme';

  if(gradient.items === undefined || gradient.items.length === 0)
    return errMsgPrefix + 'No color entries defined';

  for(let i=0; i < gradient.items.length; i++)
  {
    if(gradient.items[i].value === undefined)
      return errMsgPrefix + `entry ${i+1} has an undefined value`;
    if(gradient.items[i].color === undefined || gradient.items[i].color.length === 0)
      return errMsgPrefix + `entry ${i+1} has an undefined color`;
    if(i > 0 && gradient.items[i].value <= gradient.items[i-1].value)
      return errMsgPrefix + `entries ${i} and ${i+1} values not in sorted order`;
  }

  // Success
  return '';
}
